import type { Driver, DriveStep } from 'driver.js'
import { useCallback, useEffect, useRef } from 'react'
import { startGuidedTour } from '~/components/Global/Onboarding/startGuidedTour'
import { useUpdateConfig } from '~/domains/configuration/mutations/updateConfig'
import { useConfig } from '~/domains/configuration/queries/config'

const steps: DriveStep[] = [
  {
    popover: {
      title: 'Bienvenue dans vos tâches',
      description:
        "Retrouvez ici tout ce que vous avez à faire : appels à passer, tâches libres et tâches personnalisées, liées à un candidat, à un contact ou à personne. Ce rapide tutoriel vous présente l'essentiel.",
      side: 'bottom',
      align: 'center'
    }
  },
  {
    element: '[data-onboarding="tasks-create"]',
    popover: {
      title: 'Créez une tâche',
      description:
        'Un appel, une tâche libre avec son propre sujet ou une tâche personnalisée. Liez-la à un ou plusieurs candidats ou contacts, planifiez-la et assignez-la à vous-même ou à un collègue.',
      side: 'left',
      align: 'start'
    }
  },
  {
    element: '[data-onboarding="tasks-tabs"]',
    popover: {
      title: 'À faire ou terminées',
      description:
        "« À faire » regroupe les tâches ouvertes, classées par échéance : en retard, aujourd'hui, cette semaine… « Terminées » garde la trace de ce qui a été traité, des plus récentes aux plus anciennes.",
      side: 'bottom',
      align: 'start'
    }
  },
  {
    element: '[data-onboarding="tasks-filters"]',
    popover: {
      title: 'Filtrez vos tâches',
      description:
        'Par défaut, vous voyez les tâches qui vous sont assignées. Filtrez par collaborateur, agence, type, personne liée ou échéance, et enregistrez vos recherches pour les retrouver en un clic.',
      side: 'bottom',
      align: 'start'
    }
  },
  {
    element: '[data-onboarding="task-card"]',
    popover: {
      title: 'Traitez et modifiez',
      description:
        "Cliquez sur une tâche pour l'ouvrir : indiquez son issue, décalez son échéance, réassignez-la à un collègue ou complétez vos notes.",
      side: 'bottom',
      align: 'start'
    }
  },
  {
    element: '[data-onboarding="task-persons"]',
    popover: {
      title: 'Contactez sans quitter la page',
      description:
        'Survolez les personnes liées pour les appeler, leur envoyer un SMS ou un email, ou ouvrir leur fiche.',
      side: 'bottom',
      align: 'center'
    }
  },
  {
    element: '[data-onboarding="task-quick-done"]',
    popover: {
      title: 'Terminez en un clic',
      description:
        'Tâche faite ? Cochez-la : elle passe directement dans « Terminées ». Pour préciser un appel non abouti, ouvrez plutôt la tâche.',
      side: 'left',
      align: 'center'
    }
  },
  {
    element: '[data-onboarding="tasks-tour"]',
    popover: {
      title: 'À vous de jouer !',
      description:
        'Vous pouvez relancer ce tutoriel à tout moment depuis ce bouton.',
      side: 'left',
      align: 'start'
    }
  }
]

/** Page tour of the tasks list, started once automatically as soon as the page is `ready`. */
export const useTasksOnboarding = (ready: boolean) => {
  const { data: tourSeen } = useConfig('onboarding.tasks_tour')
  const { mutate: markTourSeen } = useUpdateConfig('onboarding.tasks_tour')
  const tourRef = useRef<Driver | null>(null)
  const autoStartedRef = useRef(false)

  const startTour = useCallback(() => {
    tourRef.current?.destroy()
    tourRef.current = startGuidedTour(steps)
  }, [])

  useEffect(() => () => tourRef.current?.destroy(), [])

  useEffect(() => {
    if (!ready || tourSeen !== false || autoStartedRef.current) {
      return
    }
    autoStartedRef.current = true
    markTourSeen({ value: true })
    startTour()
  }, [ready, tourSeen, markTourSeen, startTour])

  return { startTour }
}
